import * as React from "react";
import { Button, Chip } from "@material-tailwind/react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { Link, useNavigate } from "react-router-dom";

import { userAccountController } from "../../../Common/Controllers/UserAccountController";
import { alertController } from "../../../Common/Controllers/AlertController";
import { Dialog } from "./Dialog";
import { StockistIssue } from "../../../Common/DTOs/StockistIssue";
import { loginSignupController } from "../../../Common/Components/Standalone/LoginOrSignup/LoginSignupController";
import { issueDetailsController } from "../Controllers/IssueDetailsController";

export type IssueDetailsProps = {
  issue: StockistIssue
}

type IssueProps = {
  issue: StockistIssue
  onlyThumbnail: boolean
  TitleDetails: React.FC<IssueDetailsProps>
  isHighlighted?: boolean
  labelText?: string
  labelTextColorClass?: string
}

export const Issue = observer(React.forwardRef(({ issue, onlyThumbnail, TitleDetails, isHighlighted, labelText, labelTextColorClass }: IssueProps, ref: React.Ref<HTMLDivElement>) => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const buttonSize = windowSize < 880 ? 'md' : 'lg';
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onBuyButtonClick = () => {
    if (!userAccountController.isLoggedIn) {
      alertController.displayAlert(
        "Login required",
        "You have to be logged in to buy magazines.",
        "warning",
        4000
      )
      loginSignupController.setLoginSignupWindowVisible(true)
      return;
    }
    setOrderDialogOpen(true);
  }

  return (
    <div className={`flex flex-col items-center justify-between animate-fadeUp w-full px-2 xsm:px-0 xsm:w-[230px] ${onlyThumbnail ? "h-[380px]" : "h-[489px]"} ${isHighlighted ? "outline outline-3 outline-purple outline-offset-2 shadow shadow-purple" : ""}`} ref={ref}>
      <div className="relative h-[90%] w-full">
        <div className={classNames("absolute h-full w-full flex flex-col items-center sm:items-start gap-0.5", {
          "!opacity-[10%] pointer-events-none": detailsOpen,
          "opacity-100 animate-fadeUp": !detailsOpen
        })}>
          <div className="relative d-inline-block mx-auto w-full transition duration-150 ease-in-out hover:shadow-[0_0_12px_#fff] hover:shadow-white">
            <img
              src={issue.imageURL_230x320}
              className="mx-auto w-[230px] h-[320px] cursor-pointer"
              alt={`${issue.identity} cover`}
              title={`See this issue of ${issue.magazine.titleDisplay}`}
              onClick={() => { issueDetailsController.setMagazineHint(issue.magazineId); navigate(`/issue/${issue.id}`); }}
            />
            {labelText && <Chip color="blue" value={labelText} className={classNames(`absolute bottom-0 left-0 bg-white text-sm px-1 md:px-2 py-1 rounded-none font-semibold border-t border-r border-main ${labelTextColorClass}`)} />}
          </div>
          {!onlyThumbnail &&
            <div className="flex flex-col mt-4 gap-2 w-full items-center xsm:items-start justify-between flex-auto">
              <div className="flex flex-col items-center xsm:items-start gap-1 w-full">
                <div className="text-xl font-bold w-full truncate text-center xsm:text-left">
                  <Link
                    className="transition duration-150 ease-in text-white hover:text-gray-400 cursor-pointer"
                    title={`See latest issues of ${issue.magazine.titleDisplay}`}
                    to={issue.magazine.stockistMagazineUrl}
                    onClick={() => issueDetailsController.setMagazineHint(issue.magazineId)}
                  >
                    {issue.magazine.titleDisplay}
                  </Link>
                </div>
                <div className="text-gray-500 flex justify-center xsm:justify-start gap-1 w-full">
                  <span title={issue.identity} className="truncate">{issue.identity}</span>
                  <span>|</span>
                  <span title={issue.onSaleDateDisplay}>{issue.onSaleDateDisplay}</span>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <div className="text-xl font-bold">RRP {issue.priceDisplay}</div>
                <span className="whitespace-nowrap text-gray-500">
                  ({issue.stock.issuesAvailableIncludingBasket > 30 ? "30+" : issue.stock.issuesAvailableIncludingBasket} in stock)
                </span>
              </div>
            </div>
          }
        </div>
        <div className={classNames("absolute h-full pb-4 w-full",
          {
            "opacity-0 hidden pointer-events-none": !detailsOpen,
            "opacity-100": detailsOpen
          }
        )}
          onClick={() => setDetailsOpen(false)}
        >
          <TitleDetails issue={issue} />
        </div>
      </div>

      <div className="h-auto w-full flex flex-row justify-between gap-1 w-[230px] pt-2 border-t border-gray-700">
        <Button
          className={"rounded-none text-lg sm:text-sm !px-3 sm:!px-6 !py-2 sm:!py-4 sm:rounded-full h-[50px] w-1/2 whitespace-nowrap"}
          color={"white"} size={buttonSize}
          onClick={() => onBuyButtonClick()}
          disabled={issue.stock.issuesAvailableIncludingBasket <= 0}
        >
          Buy Now
        </Button>
        <Button
          className={classNames("transition text-lg sm:text-sm !px-3 sm:!px-6 !py-2 sm:!py-4 duration-300 ease-in-out rounded-none sm:rounded-full h-[50px] bg-black w-1/2 focus:shadow-none", {
            "!shadow-[0_0_3px_2px_#888]": detailsOpen,
            "!shadow-light-grey": detailsOpen,
            "!shadow-none": !detailsOpen
          })}
          color={"white"}
          variant={"outlined"}
          size={buttonSize}
          onClick={() => setDetailsOpen(!detailsOpen)}
        >
          Details
        </Button>
      </div>

      <Dialog
        issue={issue}
        orderDialogOpen={orderDialogOpen}
        setOrderDialogOpen={setOrderDialogOpen}
      />
    </div>
  );
}));